import {Component, Inject, Optional} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {References} from "../../../dtos/references";

export interface ReferenceSelectDialogData extends References {
  // id and type of the entity being edited, so it can be excluded from its own reference search
  selfId?: number | null;
  selfType?: string | null;
}

@Component({
    selector: 'app-reference-select',
    templateUrl: './reference-select.component.html',
    styleUrl: './reference-select.component.scss',
    standalone: false
})
export class ReferenceSelectComponent {

  selectionMode = 'multi-select'

  //vocab entries
  selectedVocEnt = []
  displayedColumnsVocEnt = ['multi-select', 'voc-ent-name', 'voc-ent-cat']
  excludedIdVocEnt = null

  //vocab categories
  selectedVocCat = []
  displayedColumnsVocCat = ['multi-select', 'voc-cat-name', 'voc-cat-parent', 'voc-cat-type']
  excludedIdVocCat = null

  // Functional grammar entries
  selectedFunEnt = [];
  displayedColumnsFunEnt = ['multi-select', 'fun-ent-name', 'fun-ent-cat'];
  excludedIdFunEnt = null;

  // Functional grammar categories
  selectedFunCat = [];
  displayedColumnsFunCat = ['multi-select', 'fun-cat-name', 'fun-cat-parent'];
  excludedIdFunCat = null;

  // Systematic grammar
  selectedSysEnt = [];
  displayedColumnsSysGram = ['multi-select', 'sys-ent-name', 'sys-ent-cat'];
  excludedIdSysGram = null;

  // Systematic grammar categories
  selectedSysCat = [];
  displayedColumnsSysGramCat = ['multi-select', 'sys-cat-name', 'sys-cat-parent'];
  excludedIdSysGramCat = null;

  // Text types
  selectedTextTypes = [];
  displayedColumnsTextTypes = ['multi-select', 'text-type-name', 'text-type-type'];
  excludedIdTextTypes = null;

  // Text patterns
  selectedTextPatterns = [];
  displayedColumnsTextPatterns = ['multi-select', 'text-pattern-name', 'text-pattern-type'];
  excludedIdTextPatterns = null;

  // Detailed can descriptions
  selectedDetCanDesc = [];
  displayedColumnsDetCanDesc = ['multi-select', 'det-can-ent-name', 'det-can-ent-cat'];
  excludedIdDetCanDesc = null;

  // Detailed can descriptions categories
  selectedDetCanDescCat = [];
  displayedColumnsDetCanDescCat = ['multi-select', 'det-can-cat-name', 'det-can-cat-parent'];
  excludedIdDetCanDescCat = null;

  // Global can descriptions
  selectedGlobCanDesc = [];
  displayedColumnsGlobCanDesc = ['multi-select', 'glob-can-ent-name', 'glob-can-ent-cat'];
  excludedIdGlobCanDesc = null;

  // Global can descriptions categories
  selectedGlobCanDescCat = [];
  displayedColumnsGlobCanDescCat = ['multi-select', 'glob-can-cat-name', 'glob-can-cat-parent'];
  excludedIdGlobCanDescCat = null;


  constructor(private dialogRef: MatDialogRef<ReferenceSelectComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) data: ReferenceSelectDialogData) {
    // pre-select what's already linked; clone so in-dialog edits can't mutate the caller's data
    this.selectedVocEnt = [...(data?.vocabEntries ?? [])];
    this.selectedVocCat = [...(data?.vocabCategories ?? [])];
    this.selectedFunEnt = [...(data?.functionalGrammarEntries ?? [])];
    this.selectedFunCat = [...(data?.functionalGrammarCategories ?? [])];
    this.selectedSysEnt = [...(data?.systemicGrammarEntries ?? [])];
    this.selectedSysCat = [...(data?.systemicGrammarCategories ?? [])];
    this.selectedTextTypes = [...(data?.textTypes ?? [])];
    this.selectedTextPatterns = [...(data?.textPatterns ?? [])];
    this.selectedDetCanDesc = [...(data?.detailedCanDoStatementEntries ?? [])];
    this.selectedDetCanDescCat = [...(data?.detailedCanDoStatementCategories ?? [])];
    this.selectedGlobCanDesc = [...(data?.globalCanDoStatementEntries ?? [])];
    this.selectedGlobCanDescCat = [...(data?.globalCanDoStatementCategories ?? [])];

    // exclude the entity being edited from its own reference search, so it can't reference itself;
    // a self id is only applied to the tab matching selfType, since ids are not unique across domains
    const excludeSelfFrom = (type: string) => data?.selfType === type ? (data?.selfId ?? null) : null;
    this.excludedIdVocEnt = excludeSelfFrom('vocab_entry');
    this.excludedIdVocCat = excludeSelfFrom('vocab_category');
    this.excludedIdFunEnt = excludeSelfFrom('fun_entry');
    this.excludedIdFunCat = excludeSelfFrom('fun_category');
    this.excludedIdSysGram = excludeSelfFrom('sys_entry');
    this.excludedIdSysGramCat = excludeSelfFrom('sys_category');
    this.excludedIdTextTypes = excludeSelfFrom('text_type');
    this.excludedIdTextPatterns = excludeSelfFrom('text_pattern');
    this.excludedIdDetCanDesc = excludeSelfFrom('det_can_statements');
    this.excludedIdDetCanDescCat = excludeSelfFrom('det_can_statements_category');
    this.excludedIdGlobCanDesc = excludeSelfFrom('glob_can_statements');
    this.excludedIdGlobCanDescCat = excludeSelfFrom('glob_can_statements_category');
  }

  closeDialog() {
    const ref: References = {
      vocabEntries: this.selectedVocEnt,
      vocabCategories: this.selectedVocCat,
      systemicGrammarEntries: this.selectedSysEnt,
      systemicGrammarCategories: this.selectedSysCat,
      functionalGrammarEntries: this.selectedFunEnt,
      functionalGrammarCategories: this.selectedFunCat,
      textTypes: this.selectedTextTypes,
      textPatterns: this.selectedTextPatterns,
      detailedCanDoStatementEntries: this.selectedDetCanDesc,
      detailedCanDoStatementCategories: this.selectedDetCanDescCat,
      globalCanDoStatementEntries: this.selectedGlobCanDesc,
      globalCanDoStatementCategories: this.selectedGlobCanDescCat
    }
    this.dialogRef.close(ref)
  }

  returnInitialData() {
    this.dialogRef.close();
  }

  handleSelectedVocEnt($event: any) {
    this.selectedVocEnt = $event.data;
  }

  handleSelectedVocCat($event: any) {
    this.selectedVocCat = $event.data;
  }

  handleSelectedFunEnt($event: any) {
    this.selectedFunEnt = $event.data
  }

  handleSelectedFunCat($event: any) {
    this.selectedFunCat = $event.data;
  }

  handleSelectedSysEnt($event: any) {
    this.selectedSysEnt = $event.data;
  }

  handleSelectedSysCat($event: any) {
    this.selectedSysCat = $event.data;
  }

  handleSelectedTextTypes($event: any) {
    this.selectedTextTypes = $event.data;
  }

  handleSelectedTextPatterns($event: any) {
    this.selectedTextPatterns = $event.data;
  }

  handleSelectedDetCanDesc($event: any) {
    this.selectedDetCanDesc = $event.data;
  }

  handleSelectedDetCanDescCat($event: any) {
    this.selectedDetCanDescCat = $event.data;
  }

  handleSelectedGlobCanDesc($event: any) {
    this.selectedGlobCanDesc = $event.data;
  }

  handleSelectedGlobCanDescCat($event: any) {
    this.selectedGlobCanDescCat = $event.data;
  }
}
